<template>
  <div class="dash">
    <div class="dash-head">
      <div>
        <div class="dash-kicker">知行悦房旅 · 数据看板</div>
        <h2>{{ headTitle }}</h2>
      </div>
      <div class="dash-tools">
        <el-radio-group v-model="mode" size="small" @change="onModeChange">
          <el-radio-button label="year">按年</el-radio-button>
          <el-radio-button label="month">按月</el-radio-button>
        </el-radio-group>
        <el-date-picker
          v-if="mode === 'year'"
          v-model="year"
          type="year"
          value-format="yyyy"
          :clearable="false"
          size="small"
          @change="reload">
        </el-date-picker>
        <el-date-picker
          v-else
          v-model="monthValue"
          type="month"
          value-format="yyyy-MM"
          :clearable="false"
          size="small"
          @change="reload">
        </el-date-picker>
      </div>
    </div>

    <el-row :gutter="16" class="kpi-row">
      <el-col :xs="12" :sm="12" :md="6" :lg="6" v-for="item in kpis" :key="item.key">
        <div class="kpi" :style="{ '--accent': item.color }">
          <div class="kpi-label">{{ item.label }}</div>
          <div class="kpi-value">{{ item.value }}</div>
          <div class="kpi-sub">{{ mode === 'year' ? '本年累计' : '本月累计' }}</div>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :sm="12" :md="12" :lg="12" v-for="chart in charts" :key="chart.ref">
        <div class="chart-card">
          <div class="chart-title">
            <span class="dot" :style="{ background: chart.color }"></span>
            <span class="chart-name">{{ chart.title }}</span>
            <el-select
              class="chart-type"
              size="mini"
              v-model="chartKinds[chart.ref]"
              @change="draw(chart)">
              <el-option
                v-for="item in chartTypeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value">
              </el-option>
            </el-select>
          </div>
          <div class="chart-el" :ref="chart.ref"></div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import echarts from 'echarts'
import { monthlyStats, dailyStats } from '../../api/spAdmin'

const MONTHS = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
const EMPTY = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
const PIE_PALETTE = ['#5b67f1', '#8b5cf6', '#14b8a6', '#f59e0b', '#ff6b6b', '#38bdf8', '#ec4899', '#84cc16']
const CHART_TYPE_OPTIONS = [
  { value: 'line', label: '折线图' },
  { value: 'bar', label: '柱状图' },
  { value: 'pie', label: '扇形图' },
  { value: 'radar', label: '雷达图' }
]

function pad(n) {
  return n < 10 ? '0' + n : '' + n
}

function currentMonthValue() {
  const d = new Date()
  return d.getFullYear() + '-' + pad(d.getMonth() + 1)
}

export default {
  name: 'dashboard',
  data() {
    return {
      mode: 'year',
      year: String(new Date().getFullYear()),
      monthValue: currentMonthValue(),
      stats: {
        houseOnShelf: EMPTY.slice(),
        visits: EMPTY.slice(),
        newUsers: EMPTY.slice(),
        sellIntents: EMPTY.slice(),
        houseTotal: 0,
        visitTotal: 0,
        userTotal: 0,
        intentTotal: 0
      },
      instances: {},
      chartTypeOptions: CHART_TYPE_OPTIONS,
      chartKinds: {
        houseChart: 'line',
        visitChart: 'line',
        userChart: 'line',
        intentChart: 'line'
      }
    }
  },
  computed: {
    headTitle() {
      if (this.mode === 'month') {
        const p = (this.monthValue || currentMonthValue()).split('-')
        return Number(p[0]) + ' 年 ' + Number(p[1]) + ' 月每日情况'
      }
      const y = this.year instanceof Date ? this.year.getFullYear() : this.year
      return y + ' 年经营概览'
    },
    axisLabels() {
      if (this.mode === 'month') {
        const n = (this.stats.houseOnShelf || []).length || 30
        const list = []
        for (let i = 1; i <= n; i++) list.push(i + '日')
        return list
      }
      return MONTHS
    },
    charts() {
      const daily = this.mode === 'month'
      return [
        { ref: 'houseChart', title: daily ? '每日租房上架数量' : '每月租房上架数量', color: '#5b67f1', key: 'houseOnShelf' },
        { ref: 'visitChart', title: daily ? '每日预约看房数量' : '每月预约看房数量', color: '#f59e0b', key: 'visits' },
        { ref: 'userChart', title: daily ? '每日用户新增数量' : '每月用户新增数量', color: '#14b8a6', key: 'newUsers' },
        { ref: 'intentChart', title: daily ? '每日卖房意向新增' : '每月卖房意向新增', color: '#ff6b6b', key: 'sellIntents' }
      ]
    },
    kpis() {
      return [
        { key: 'house', label: '租房上架', value: this.stats.houseTotal, color: '#5b67f1' },
        { key: 'visit', label: '预约看房', value: this.stats.visitTotal, color: '#f59e0b' },
        { key: 'user', label: '新增用户', value: this.stats.userTotal, color: '#14b8a6' },
        { key: 'intent', label: '卖房意向', value: this.stats.intentTotal, color: '#ff6b6b' }
      ]
    }
  },
  mounted() {
    this.reload()
    window.addEventListener('resize', this.resize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
    Object.keys(this.instances).forEach(key => {
      this.instances[key] && this.instances[key].dispose()
    })
  },
  methods: {
    reload() {
      const req = this.mode === 'month' ? this.loadDaily() : this.loadYearly()
      req.then(res => {
        if (!res || res.status !== 200) {
          throw new Error((res && res.message) || '统计接口失败')
        }
        const data = (res && res.data) || {}
        this.stats = {
          houseOnShelf: data.houseOnShelf || EMPTY.slice(),
          visits: data.visits || EMPTY.slice(),
          newUsers: data.newUsers || EMPTY.slice(),
          sellIntents: data.sellIntents || EMPTY.slice(),
          houseTotal: data.houseTotal || 0,
          visitTotal: data.visitTotal || 0,
          userTotal: data.userTotal || 0,
          intentTotal: data.intentTotal || 0
        }
        this.$nextTick(this.drawAll)
      }).catch(() => {
        this.$message.warning('统计接口暂不可用，已展示空数据')
        this.$nextTick(this.drawAll)
      })
    },
    loadYearly() {
      var y = this.year instanceof Date ? this.year.getFullYear() : Number(this.year)
      return monthlyStats(y)
    },
    loadDaily() {
      const p = (this.monthValue || currentMonthValue()).split('-')
      return dailyStats(Number(p[0]), Number(p[1]))
    },
    onModeChange() {
      this.reload()
    },
    drawAll() {
      this.charts.forEach(chart => {
        this.draw(chart)
      })
    },
    draw(chart) {
      const el = this.$refs[chart.ref]
      const node = Array.isArray(el) ? el[0] : el
      if (!node) return
      if (!this.instances[chart.ref]) {
        this.instances[chart.ref] = echarts.init(node)
      }
      const kind = this.chartKinds[chart.ref] || 'line'
      const option = this.buildOption(chart, kind)
      this.instances[chart.ref].clear()
      this.instances[chart.ref].setOption(option, true)
    },
    buildOption(chart, kind) {
      const values = this.stats[chart.key] || EMPTY
      const labels = this.axisLabels
      if (kind === 'bar') return this.barOption(chart, labels, values)
      if (kind === 'pie') return this.pieOption(chart, labels, values)
      if (kind === 'radar') return this.radarOption(chart, labels, values)
      return this.lineOption(chart, labels, values)
    },
    axisTooltip() {
      return {
        trigger: 'axis',
        backgroundColor: 'rgba(31,22,51,.92)',
        borderWidth: 0,
        textStyle: { color: '#fff' }
      }
    },
    itemTooltip() {
      return {
        trigger: 'item',
        backgroundColor: 'rgba(31,22,51,.92)',
        borderWidth: 0,
        textStyle: { color: '#fff' }
      }
    },
    lineOption(chart, labels, values) {
      return {
        color: [chart.color],
        tooltip: this.axisTooltip(),
        grid: { left: 42, right: 18, top: 28, bottom: 28 },
        xAxis: {
          type: 'category',
          data: labels,
          axisLine: { lineStyle: { color: '#d8dee8' } },
          axisLabel: { color: '#8a93a6', interval: this.mode === 'month' ? 1 : 0 },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          splitLine: { lineStyle: { color: '#eef1f6', type: 'dashed' } },
          axisLabel: { color: '#8a93a6' }
        },
        series: [{
          name: chart.title,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: { width: 3 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: this.hex(chart.color, 0.35) },
              { offset: 1, color: this.hex(chart.color, 0.02) }
            ])
          },
          data: values
        }]
      }
    },
    barOption(chart, labels, values) {
      return {
        color: [chart.color],
        tooltip: this.axisTooltip(),
        grid: { left: 42, right: 18, top: 28, bottom: 28 },
        xAxis: {
          type: 'category',
          data: labels,
          axisLine: { lineStyle: { color: '#d8dee8' } },
          axisLabel: { color: '#8a93a6', interval: this.mode === 'month' ? 1 : 0 },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          splitLine: { lineStyle: { color: '#eef1f6', type: 'dashed' } },
          axisLabel: { color: '#8a93a6' }
        },
        series: [{
          name: chart.title,
          type: 'bar',
          barMaxWidth: this.mode === 'month' ? 10 : 22,
          itemStyle: {
            barBorderRadius: [8, 8, 0, 0],
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: chart.color },
              { offset: 1, color: this.hex(chart.color, 0.35) }
            ])
          },
          data: values
        }]
      }
    },
    pieOption(chart, labels, values) {
      const data = labels.map((name, i) => ({
        name: name,
        value: Number(values[i]) || 0
      })).filter(item => item.value > 0)
      return {
        color: PIE_PALETTE,
        tooltip: this.itemTooltip(),
        legend: {
          type: 'scroll',
          bottom: 0,
          textStyle: { color: '#6b6480', fontSize: 11 }
        },
        series: [{
          name: chart.title,
          type: 'pie',
          radius: ['36%', '62%'],
          center: ['50%', '46%'],
          minAngle: 2,
          label: { formatter: '{b}\n{c}' },
          data: data.length ? data : [{ name: '暂无数据', value: 0 }]
        }]
      }
    },
    radarOption(chart, labels, values) {
      const max = Math.max.apply(null, values.concat([1]))
      return {
        color: [chart.color],
        tooltip: this.itemTooltip(),
        radar: {
          indicator: labels.map(name => ({ name: name, max: max })),
          splitNumber: 4,
          name: { color: '#8a93a6', fontSize: this.mode === 'month' ? 10 : 12 },
          splitLine: { lineStyle: { color: '#ece8f7' } },
          splitArea: { areaStyle: { color: ['rgba(91,103,241,0.04)', 'rgba(255,255,255,0.4)'] } }
        },
        series: [{
          name: chart.title,
          type: 'radar',
          symbol: 'circle',
          symbolSize: 5,
          areaStyle: { color: this.hex(chart.color, 0.22) },
          lineStyle: { width: 2, color: chart.color },
          data: [{ value: values, name: chart.title }]
        }]
      }
    },
    hex(color, alpha) {
      const hex = color.replace('#', '')
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')'
    },
    resize() {
      Object.keys(this.instances).forEach(key => {
        this.instances[key] && this.instances[key].resize()
      })
    }
  }
}
</script>

<style scoped>
.dash {
  padding: 8px 4px 24px;
}
.dash-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
}
.dash-tools {
  display: flex;
  align-items: center;
}
.dash-tools >>> .el-radio-group {
  margin-right: 12px;
}
.dash-kicker {
  color: #8b5cf6;
  font-size: 13px;
  letter-spacing: 1px;
  margin-bottom: 6px;
  font-weight: 600;
}
.dash-head h2 {
  margin: 0;
  color: #1f1633;
  font-size: 24px;
}
.kpi-row {
  margin-bottom: 16px;
}
.kpi {
  background: #fff;
  border-radius: 18px;
  padding: 18px 20px;
  box-shadow: 0 10px 28px rgba(91, 103, 241, 0.08);
  border: 1px solid rgba(255,255,255,.8);
  position: relative;
  overflow: hidden;
}
.kpi:after {
  content: '';
  position: absolute;
  right: -12px;
  top: -12px;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--accent);
  opacity: 0.16;
}
.kpi-label {
  color: #6b6480;
  font-size: 13px;
}
.kpi-value {
  font-size: 32px;
  font-weight: 800;
  color: var(--accent);
  margin: 8px 0 4px;
}
.kpi-sub {
  color: #9b95ae;
  font-size: 12px;
}
.chart-card {
  background: #fff;
  border-radius: 18px;
  padding: 16px 16px 8px;
  margin-bottom: 16px;
  box-shadow: 0 10px 28px rgba(91, 103, 241, 0.08);
}
.chart-title {
  display: flex;
  align-items: center;
  font-weight: 700;
  color: #1f1633;
  margin-bottom: 4px;
}
.chart-name {
  flex: 1;
  min-width: 0;
}
.chart-type {
  width: 108px;
  font-weight: 500;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
}
.chart-el {
  height: 280px;
}

@media (orientation: landscape) and (max-height: 540px) {
  .dash {
    padding: 0 0 8px;
  }
  .dash-head {
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 10px;
  }
  .dash-head h2 {
    font-size: 18px;
  }
  .dash-tools {
    margin-top: 8px;
    flex-wrap: wrap;
  }
  .kpi {
    padding: 10px 12px;
    border-radius: 12px;
    margin-bottom: 10px;
  }
  .kpi-value {
    font-size: 22px;
    margin: 4px 0 2px;
  }
  .chart-el {
    height: 180px;
  }
}

@media (orientation: landscape) and (min-width: 768px) and (max-width: 1366px) {
  .dash-head {
    flex-wrap: wrap;
    gap: 10px;
  }
  .kpi {
    margin-bottom: 12px;
    padding: 14px 16px;
  }
  .kpi-value {
    font-size: 26px;
  }
  .chart-el {
    height: 240px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/home/dashboard.vue